import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { BRAND, COACHING_OUTCOMES } from "@/content";
import { submitCoaching } from "@/api";

const initial = { name: "", email: "", phone: "", focus_area: "", message: "" };

export default function Coaching() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please complete name and email.");
      return;
    }
    setSending(true);
    try {
      await submitCoaching(form);
      setDone(true); setForm(initial);
      toast.success("Inquiry received. Booking link sent to your email.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="page-coaching" className="bg-white">
      <section className="pt-[120px] pb-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="overline">Coaching</p>
            <h1 className="display mt-6 text-[44px] md:text-[120px]">
              Stop performing.<br/>Start <em className="font-serif text-[var(--ic-accent)]">choosing</em>.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="font-sans text-[17px] leading-[1.75] text-black/75">
              One-on-one coaching with me, for women ready to translate breakthrough into a
              life that actually looks different by next quarter.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-y border-[var(--ic-line)] bg-[var(--ic-warm)]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Who it's for</p>
            <h2 className="section-display mt-4">For the woman<br/>who knows.</h2>
          </div>
          <div className="col-span-12 lg:col-span-8 font-sans text-[17px] leading-[1.8] text-black/85 space-y-5">
            <p>You don't need another podcast, another planner, or another book.</p>
            <p>
              You need a room (even a virtual one) where someone sits across from you and refuses
              to let you keep editing yourself for everyone else's comfort.
            </p>
            <p>If that sounds inconvenient, it is. It's also the work.</p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Philosophy</p>
            <h2 className="section-display mt-4">Choice over<br/>strategy.</h2>
            <p className="font-sans text-[15.5px] text-black/70 mt-6 max-w-sm">
              Most coaching teaches you better strategies. My coaching teaches you to make
              better choices &mdash; and then we build the strategies on top of that foundation.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <p className="overline mb-4">Outcomes</p>
            <ul className="divide-y divide-[var(--ic-line)] border-t border-b border-[var(--ic-line)]">
              {COACHING_OUTCOMES.map((o, i) => (
                <li key={i} className="flex gap-5 items-start py-5" data-testid={`coaching-outcome-${i}`}>
                  <CheckCircle2 size={20} className="mt-1 text-black shrink-0" />
                  <span className="font-serif text-[22px] md:text-[26px] leading-[1.25] text-black">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-black text-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <p className="overline text-white/55">Book a consultation</p>
            <h2 className="display mt-6 text-white text-[44px] md:text-[88px]">
              Your first call is <em className="font-serif text-[var(--ic-accent)]">free</em>.
            </h2>
            <p className="font-sans text-[17px] text-white/75 mt-6 max-w-xl">
              A 30-minute conversation to see if working together makes sense.
              No pitch. No pressure. Just honesty about where you are and where you want to go.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="coaching-cta-calendly"
               className="inline-flex items-center gap-3 bg-white text-black px-8 py-5 font-sans text-[13px] tracking-[0.18em] uppercase hover:bg-[var(--ic-warm)] transition-colors">
              Book on Calendly <ArrowUpRight size={18}/>
            </a>
            <p className="text-white/55 text-[12px] mt-4">calendly.com/crystalpurdue30</p>
          </div>
        </div>
      </section>

      <section id="consult" className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Or send a message</p>
            <h2 className="section-display mt-4">Tell us where<br/>you are.</h2>
            <p className="font-sans text-[15px] text-black/65 mt-6 max-w-sm">
              Prefer to share context first? Send me a note. I'll reply with a booking link
              and a few questions to make the most of your call.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8">
            {done ? (
              <div className="border border-[var(--ic-line)] p-10" data-testid="coaching-success">
                <p className="overline">Inquiry received</p>
                <h3 className="font-serif text-[34px] mt-4">Check your inbox.</h3>
                <p className="font-sans text-[16px] text-black/70 mt-4 max-w-xl">
                  We sent you a confirmation with your booking link. You can also book directly:
                </p>
                <a href={BRAND.calendly} target="_blank" rel="noreferrer" className="btn-ink mt-6 inline-flex">Book Your Consultation</a>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-12 gap-6" data-testid="coaching-form">
                <FieldText col={6} label="Name *"  v={form.name} on={onChange("name")} testid="co-name"/>
                <FieldText col={6} label="Email *" v={form.email} on={onChange("email")} type="email" testid="co-email"/>
                <FieldText col={6} label="Phone"   v={form.phone} on={onChange("phone")} testid="co-phone"/>
                <FieldText col={6} label="What do you want to work on?" v={form.focus_area} on={onChange("focus_area")} testid="co-focus"/>
                <FieldArea col={12} label="A little context" v={form.message} on={onChange("message")} testid="co-message"/>
                <div className="col-span-12">
                  <button type="submit" disabled={sending} data-testid="co-submit" className="btn-ink disabled:opacity-50">
                    {sending ? "Sending…" : "Send Coaching Inquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldText({ col=12, label, v, on, type="text", testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <input data-testid={testid} type={type} value={v} onChange={on} className="ed-input" />
    </div>
  );
}
function FieldArea({ col=12, label, v, on, testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <textarea data-testid={testid} value={v} onChange={on} className="ed-textarea" rows={5} />
    </div>
  );
}
